// packages/shared/src/utils/country.ts
//
// Canonical normaliser: free-text country input → ISO-3166 α2 (uppercase).
// Returns null for unrecognisable input. Drives the P3 ingest-layer normaliser
// and the migration backfill for threat_actors.originCountry +
// regulatory_actions.jurisdiction.
//
// Captain Input #1: ISO-3166 α2 in DB; display layer maps to localised names.
// Adding new variants here is the canonical way to extend coverage as we
// encounter new upstream-source phrasings.

import { countries } from '../ontology/dimensions/countries.js';

// Build the canonical α2 set from the synced ontology so the ontology is the
// source of truth — adding a new α2 to countries.ts automatically widens
// what `normaliseToAlpha2` returns.
const CANONICAL_ALPHA2 = new Set(countries.map((c) => c.slug.toUpperCase()));

// Variant aliases → α2. Extend as upstream sources surface new phrasings.
// Keys are uppercased for case-insensitive lookup.
const VARIANT_TO_ALPHA2: Record<string, string> = {
  // English variants
  'USA': 'US',
  'U.S.': 'US',
  'U.S.A.': 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  'UK': 'GB',
  'GREAT BRITAIN': 'GB',
  'UNITED KINGDOM': 'GB',
  'BRITAIN': 'GB',
  'CZECHIA': 'CZ',
  'CZECH REPUBLIC': 'CZ',
  'SOUTH KOREA': 'KR',
  'REPUBLIC OF KOREA': 'KR',
  'NORTH KOREA': 'KP',
  'DPRK': 'KP',
  "DEMOCRATIC PEOPLE'S REPUBLIC OF KOREA": 'KP',
  'RUSSIAN FEDERATION': 'RU',

  // DPA-source variants observed in regulatory_actions.jurisdiction (2026-05-03 prod query)
  'THE NETHERLANDS': 'NL',
  'NETHERLANDS': 'NL',
  'ISLE OF MAN': 'IM',

  // Localised / DPA-source variants observed in production data
  'ESPAÑA': 'ES',
  'ITALIA': 'IT',
  'DEUTSCHLAND': 'DE',
  // Add as encountered.
};

// Build full English-name index from the ontology.
const ENGLISH_NAME_TO_ALPHA2 = new Map<string, string>(
  countries.map((c) => [c.name.toUpperCase(), c.slug.toUpperCase()]),
);

// Sentinel non-country inputs that should resolve to null instead of
// being misinterpreted by a partial match.
const NULL_SENTINELS = new Set(['UNKNOWN', 'N/A', 'NULL', 'NONE', '-', '?']);

/**
 * Normalise free-text country input to ISO-3166 α2 (uppercase).
 * Returns null for null/empty/whitespace/sentinel/unrecognisable input.
 */
export function normaliseToAlpha2(input: string | null | undefined): string | null {
  if (input === null || input === undefined) return null;
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  const upper = trimmed.toUpperCase();
  if (NULL_SENTINELS.has(upper)) return null;

  // Direct α2 match
  if (upper.length === 2 && CANONICAL_ALPHA2.has(upper)) {
    return upper;
  }
  // Variant alias
  if (VARIANT_TO_ALPHA2[upper]) {
    return VARIANT_TO_ALPHA2[upper];
  }
  // Canonical English name from the ontology
  if (ENGLISH_NAME_TO_ALPHA2.has(upper)) {
    return ENGLISH_NAME_TO_ALPHA2.get(upper)!;
  }

  return null;
}
